import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteImageUrl } from "@/lib/schema-org";
import { useState } from "react";
import { REALISATIONS, REALISATION_CATEGORIES } from "@/data/realisations";
import { TldrBox } from "@/components/tldr-box";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import heroImg from "@/assets/dj-mariage-sparks.jpg";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
export const Route = createFileRoute("/realisations")({
  head: () => ({
    meta: [
      { title: "Réalisations DJ mariage en Provence | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Portfolio des événements animés par DJ Val : mariages en château, soirées d'entreprise et soirées à thème en Provence, dans le Vaucluse et les Bouches-du-Rhône.")
      },
      { property: "og:title", content: "Réalisations DJ Mariage Provence | DJ Val" },
      { property: "og:description", content: metaDesc("12 événements détaillés : châteaux, bastides, domaines viticoles, salles d'entreprise.") },
      { property: "og:url", content: "https://dj-val.fr/realisations" },
      { property: "og:image", content: absoluteImageUrl(REALISATIONS[0].image) },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/realisations" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Réalisations DJ Val",
          description: "Portfolio des événements animés par DJ Val en Provence",
          image: REALISATIONS.map((r) => ({
            "@type": "ImageObject",
            contentUrl: r.image,
            name: r.title,
            description: r.description,
            contentLocation: r.city,
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  const [filter, setFilter] = useState<(typeof REALISATION_CATEGORIES)[number]>("Tous");
  const filtered = filter === "Tous" ? REALISATIONS : REALISATIONS.filter((r) => r.category === filter);

  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Portfolio événements"
        title={<><span className="italic">Réalisations</span> récentes</>}
        subtitle="12 événements détaillés parmi les centaines animés depuis 20 ans : châteaux du Luberon, bastides provençales, domaines viticoles, salles corporate."
        primary={{ label: "Voir la galerie", to: "/galerie" }}
        secondary={{ label: "Demander un devis", to: "/contactez-nous" }}
        height="compact"
      />
      <ReviewsProof />


      <div className="px-6">
        <TldrBox
          title="En bref"
          summary="DJ Val a animé plus de 500 événements en 20 ans : mariages dans les plus belles bastides du Luberon, soirées corporate jusqu'à 350 personnes, anniversaires marquants. Setup pro Pioneer / RCF / Shure, équipe expérimentée."
          items={[
            { label: "Événements animés", value: "500+ en 20 ans" },
            { label: "Capacité max", value: "Jusqu'à 350 invités" },
            { label: "Lieux récurrents", value: "Châteaux, bastides, domaines viticoles" },
            { label: "Spécialités", value: "Mariages, corporate, anniversaires, thématiques" },
          ]}
        />
      </div>

      <section className="py-8 px-6 sticky top-16 lg:top-20 z-30 bg-background/95 backdrop-blur border-b border-foreground/10">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
          {REALISATION_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                filter === c
                  ? "border-[var(--gold)] text-foreground bg-[var(--gold)]/10"
                  : "border-foreground/10 text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 max-w-7xl mx-auto">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => (
            <article
              key={r.id}
              className="reveal group border border-foreground/10 bg-card/40 overflow-hidden hover:border-[var(--gold)]/40 transition-colors"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={r.image}
                  alt={r.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--gold)]">
                  {r.category} · {r.guests} invités
                </p>
                <h2 className="font-serif-display text-foreground text-xl leading-tight">{r.title}</h2>
                <p className="text-xs text-muted-foreground">
                  {r.city}
                  {r.venue ? ` · ${r.venue}` : ""}
                </p>
                <p className="text-sm font-light text-foreground/90 leading-relaxed">{r.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 bg-card border-y border-[var(--gold-line)] text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground">
            Votre événement mérite sa propre page
          </h2>
          <span className="gold-rule mx-auto !w-12" />
          <p className="text-sm font-light">
            Demandez votre devis personnalisé sous 24 h.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Link
              to="/contactez-nous"
              className="inline-block px-8 py-3 border border-[var(--gold)] text-foreground text-xs uppercase tracking-[0.3em] hover:bg-[var(--gold)]/10"
            >
              Demander un devis
            </Link>
            <Link
              to="/avis-clients"
              className="inline-block px-8 py-3 bg-[var(--gold)] text-[var(--ink)] text-xs uppercase tracking-[0.3em]"
            >
              Lire les avis clients
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
